import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Calendar, CheckCircle, XCircle, Clock, Warning, Info } from '@phosphor-icons/react';
import { formatDateTime } from '../lib/utils';

/**
 * Staff/Sub-staff: Create new Amazon account requests + view own request history.
 *
 * Feb 2026 policy:
 *  • BULK request OFF — every request grants exactly 1 account.
 *  • Reason MANDATORY — must come from a fixed dropdown.
 *  • Auto-mode (admin) still allowed; honours the same 1-per-request rule.
 */
export default function MyAccountRequestsPage() {
  const [items, setItems] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ agency_name: '', reason: '', reason_extra: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: rs }] = await Promise.all([
        api.get('/account-requests'),
        api.get('/account-requests/allowed-reasons').catch(() => ({ data: { reasons: [] } })),
      ]);
      setItems(data || []);
      setReasons((rs && rs.reasons) || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load fail');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.agency_name.trim()) return toast.error('Agency name required');
    if (!form.reason) return toast.error('Reason select karna mandatory hai');
    if (form.reason === 'Other' && form.reason_extra.trim().length < 3) {
      return toast.error("'Other' choose kiya hai → specific reason 3+ characters me likho");
    }
    setSubmitting(true);
    try {
      const payload = {
        agency_name: form.agency_name.trim(),
        pending_quantity: 1,            // bulk OFF — fixed at 1
        reason: form.reason,
        reason_extra: form.reason_extra.trim(),
      };
      const { data } = await api.post('/account-requests', payload);
      toast.success(
        data.auto_approved
          ? `✅ Auto-approved! 1 account allocated`
          : `Request submitted — admin approve karega`,
      );
      setOpen(false);
      setForm({ agency_name: '', reason: '', reason_extra: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit fail');
    } finally {
      setSubmitting(false);
    }
  };

  const StatusBadge = ({ s }) => {
    const map = {
      pending:           { c: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock, label: 'PENDING' },
      fulfilled:         { c: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle, label: 'FULFILLED' },
      partial:           { c: 'bg-blue-100 text-blue-700 border-blue-300', icon: Warning, label: 'PARTIAL' },
      approved_no_stock: { c: 'bg-orange-100 text-orange-700 border-orange-300', icon: Warning, label: 'NO STOCK' },
      rejected:          { c: 'bg-red-100 text-red-700 border-red-300', icon: XCircle, label: 'REJECTED' },
    };
    const cfg = map[s] || { c: 'bg-gray-100 text-gray-700 border-gray-300', icon: Clock, label: s.toUpperCase() };
    const Icon = cfg.icon;
    return <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded border ${cfg.c}`}><Icon size={10} weight="fill" /> {cfg.label}</span>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Account Requests</h1>
          <p className="text-sm text-gray-500 mt-0.5">Naya Amazon account chahiye? Reason batao · Admin 1 account approve karega · Bulk request OFF</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-violet-600 hover:bg-violet-700" data-testid="new-request-btn">
          <Plus size={16} className="mr-1" /> New Request
        </Button>
      </div>

      <div className="card-flat overflow-x-auto">
        <Table className="table-dense">
          <TableHeader>
            <TableRow>
              <TableHead>Agency</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="text-right">Allocated</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Approved By</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-gray-500">Loading…</TableCell></TableRow> :
             items.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-gray-500">Koi request nahi · "+ New Request" se bana sakte ho</TableCell></TableRow> :
             items.map(r => (
              <TableRow key={r.id} data-testid={`mar-row-${r.id}`}>
                <TableCell className="text-sm font-semibold">{r.agency_name}</TableCell>
                <TableCell className="text-xs text-gray-700 max-w-[260px]">
                  {r.reason_code ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">
                      {r.reason_code}
                    </span>
                  ) : null}
                  {r.reason_extra && <span className="block mt-0.5 text-gray-600 truncate" title={r.reason_extra}>{r.reason_extra}</span>}
                  {!r.reason_code && r.reason && <span className="text-gray-600 truncate" title={r.reason}>{r.reason}</span>}
                </TableCell>
                <TableCell className="text-right text-sm">
                  <span className={r.allocated_count >= 1 ? 'text-emerald-700 font-bold' : 'text-amber-700'}>
                    {r.allocated_count}/1
                  </span>
                </TableCell>
                <TableCell><StatusBadge s={r.status} />{r.auto_approved && <span className="ml-1 text-[9px] bg-violet-100 text-violet-700 px-1 rounded font-bold">AUTO</span>}</TableCell>
                <TableCell className="text-xs">{r.approved_by_name || '—'}</TableCell>
                <TableCell className="text-xs text-gray-500"><Calendar size={11} className="inline mr-0.5" /> {formatDateTime(r.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* New Request dialog — Feb 2026: 1 account fixed, reason mandatory */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="new-request-dialog">
          <DialogHeader>
            <DialogTitle>New Amazon Account Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-300 rounded p-2 text-[11px] text-amber-900 flex gap-1.5 items-start">
              <Info size={14} className="text-amber-700 shrink-0 mt-0.5" weight="fill" />
              <div>
                Bulk requests OFF hain. Ek time pe sirf <b>1 account</b> milega. Aur account chahiye to dobara request karna hoga.
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Agency Name <span className="text-red-500">*</span></label>
              <Input value={form.agency_name} onChange={e => setForm({ ...form, agency_name: e.target.value })} placeholder="e.g., KISHORI INDANE" className="h-9 text-sm mt-1" data-testid="req-agency" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Reason <span className="text-red-500">*</span></label>
              <select
                value={form.reason}
                onChange={e => setForm({ ...form, reason: e.target.value })}
                className="w-full h-9 text-sm mt-1 rounded-md border border-gray-300 px-2 bg-white"
                data-testid="req-reason"
              >
                <option value="">— Select a reason —</option>
                {reasons.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <div className="text-[10px] text-gray-500 mt-1">Bina valid reason ke approval nahi hoga.</div>
            </div>
            {form.reason === 'Other' && (
              <div>
                <label className="text-xs font-semibold text-gray-700">Specific reason <span className="text-red-500">*</span></label>
                <textarea
                  value={form.reason_extra}
                  onChange={e => setForm({ ...form, reason_extra: e.target.value })}
                  placeholder="Detail likho (3+ characters)…"
                  className="w-full text-sm rounded-md border border-gray-300 p-2 h-20 mt-1"
                  data-testid="req-reason-extra"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="req-cancel">Cancel</Button>
            <Button onClick={submit} disabled={submitting || !form.agency_name.trim() || !form.reason} className="bg-violet-600 hover:bg-violet-700" data-testid="req-submit">
              {submitting ? 'Submitting…' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
